import { useMemo } from 'react';

import { graphql, useStaticQuery } from 'gatsby';
import { useIntl } from 'gatsby-plugin-intl';
import get from 'lodash/get';

export const useSiteLoadSettings = () => {
  const { locale: language, defaultLocale: defaultLanguage } = useIntl();

  const {
    settingsEN,
    settingsDE,
    settingsDEAT,
    navigationThemenEN,
    navigationThemenDE,
    navigationThemenDEAT,
    navigationFooterPagesEN,
    navigationFooterPagesDE,
    navigationFooterPagesDEAT,
    navigationMainEN,
    navigationMainDE,
    navigationMainDEAT,
    allAvailableMenus,
  } = useStaticQuery(graphql`
    query siteSettings {
      settingsEN: allStrapiGlobal(filter: { locale: { eq: "en" } }) {
        ...SiteSettingsEdges
      }
      settingsDE: allStrapiGlobal(filter: { locale: { eq: "de" } }) {
        ...SiteSettingsEdges
      }
      settingsDEAT: allStrapiGlobal(filter: { locale: { eq: "de-AT" } }) {
        ...SiteSettingsEdges
      }
      navigationThemenEN: allStrapiNavigation(filter: { name: { eq: "footerThemen" }, localeCode: { eq: "en" } }) {
        ...NavigationEdges
      }
      navigationThemenDE: allStrapiNavigation(filter: { name: { eq: "footerThemen" }, localeCode: { eq: "de" } }) {
        ...NavigationEdges
      }
      navigationThemenDEAT: allStrapiNavigation(filter: { name: { eq: "footerThemen" }, localeCode: { eq: "de-AT" } }) {
        ...NavigationEdges
      }
      navigationFooterPagesEN: allStrapiNavigation(filter: { name: { eq: "footerPages" }, localeCode: { eq: "en" } }) {
        ...NavigationEdges
      }
      navigationFooterPagesDE: allStrapiNavigation(filter: { name: { eq: "footerPages" }, localeCode: { eq: "de" } }) {
        ...NavigationEdges
      }
      navigationFooterPagesDEAT: allStrapiNavigation(
        filter: { name: { eq: "footerPages" }, localeCode: { eq: "de-AT" } }
      ) {
        ...NavigationEdges
      }
      navigationMainEN: allStrapiNavigation(filter: { localeCode: { eq: "en" }, name: { eq: "main" } }) {
        ...NavigationEdges
      }
      navigationMainDE: allStrapiNavigation(filter: { localeCode: { eq: "de" }, name: { eq: "main" } }) {
        ...NavigationEdges
      }
      navigationMainDEAT: allStrapiNavigation(filter: { localeCode: { eq: "de-AT" }, name: { eq: "main" } }) {
        ...NavigationEdges
      }
      allAvailableMenus: allStrapiNavigation {
        ...NavigationEdges
      }
    }
  `);
  const settingsMap = useMemo(
    () => ({
      en: settingsEN,
      de: settingsDE,
      'de-AT': settingsDEAT,
    }),
    [settingsEN, settingsDE, settingsDEAT]
  );

  const footerThemenMenuMap = useMemo(
    () => ({
      en: navigationThemenEN,
      de: navigationThemenDE,
      'de-AT': navigationThemenDEAT,
    }),
    [navigationThemenEN, navigationThemenDE, navigationThemenDEAT]
  );
  const footerPagesMenuMap = useMemo(
    () => ({
      en: navigationFooterPagesEN,
      de: navigationFooterPagesDE,
      'de-AT': navigationFooterPagesDEAT,
    }),
    [navigationFooterPagesEN, navigationFooterPagesDE, navigationFooterPagesDEAT]
  );
  const mainMenuMap = useMemo(
    () => ({
      en: navigationMainEN,
      de: navigationMainDE,
      'de-AT': navigationMainDEAT,
    }),
    [navigationMainEN, navigationMainDE, navigationMainDEAT]
  );
  const menusPerPage = useMemo(() => {
    const menus = get(allAvailableMenus, 'edges', []);
    const flat = menus.map(({ node }) => ({ ...node }));
    const byLocale = flat.reduce((acc, { localeCode, ...menu }) => {
      if (Object.keys(acc).includes(localeCode)) {
        acc[localeCode] = [...acc[localeCode], menu];
      } else acc[localeCode] = [menu];
      return acc;
    }, {});
    return byLocale;
  }, [allAvailableMenus]);

  const settings = useMemo(() => {
    const settingsEdge = settingsMap?.[language] || settingsMap?.[defaultLanguage];
    return get(settingsEdge, 'edges[0].node', {});
  }, [settingsMap, language]);

  const getMenu = (sourceMenu) => {
    const menu = sourceMenu?.[language] || sourceMenu?.[defaultLanguage];
    const items = get(menu, 'edges[0].node.items', []);
    if (items.length > 0) return menu;
    return sourceMenu?.[defaultLanguage] || [];
  };

  const navigation = useMemo(() => {
    return {
      main: getMenu(mainMenuMap),
      themen: getMenu(footerThemenMenuMap),
      pages: getMenu(footerPagesMenuMap),
      menusPerPage: menusPerPage?.[language] || [],
    };
  }, [footerThemenMenuMap, footerPagesMenuMap, mainMenuMap, language]);

  return {
    settings,
    navigation,
  };
};
